import { useRouter } from 'next/navigation';
import * as React from 'react';

import type { WebflowNavChromeData } from '@/hooks/useWebflowNavChrome';
import { usePermissions } from '@/hooks/usePermissions';
import { syncGuestAuthLinkVisibility } from '@/lib/webflow-poc/guest-auth-nav-links';
import { injectWebflowNavRuntime } from '@/lib/webflow-poc/inject-webflow-scripts';
import {
  WEBFLOW_NAV_CHAKRA_ISOLATION_CSS,
  WEBFLOW_NAV_HOVER_ALIGN_CSS,
  WEBFLOW_NAV_NATIVE_LAYOUT_CSS,
  WEBFLOW_NAV_THEME_MATCH_CSS,
  WEBFLOW_NAV_WRAPPER_CLASS,
} from '@/lib/webflow-poc/webflow-nav-constants';

type WebflowNavChromeProps = {
  nav: WebflowNavChromeData;
  /**
   * When true, skip TW theme-match overrides so Webflow nav background/colors
   * render as designed (WEBFLOW_NATIVE).
   */
  preserveWebflowStyles?: boolean;
};

const shouldHandleClientNavigation = (
  anchor: HTMLAnchorElement,
  event: MouseEvent
): boolean => {
  if (event.defaultPrevented) return false;
  if (event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return false;
  }
  if (anchor.target && anchor.target !== '_self') return false;
  if (anchor.hasAttribute('download')) return false;

  const href = anchor.getAttribute('href');
  if (
    !href ||
    href.startsWith('#') ||
    href.startsWith('mailto:') ||
    href.startsWith('tel:') ||
    href.startsWith('javascript:')
  ) {
    return false;
  }

  let url: URL;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return false;
  }

  if (url.origin !== window.location.origin) return false;

  return true;
};

/**
 * Renders the Webflow `#data-tradewing-nav` fragment inside the Blueprint header.
 * Same-origin links use Next client routing.
 * Guest Login/Sign up controls are hidden when the user is authenticated.
 * Injects jQuery/webflow.js so hamburger menus work on dynamic TW pages.
 */
export const WebflowNavChrome: React.FC<WebflowNavChromeProps> = ({
  nav,
  preserveWebflowStyles = false,
}) => {
  const router = useRouter();
  const permissions = usePermissions();
  const rootRef = React.useRef<HTMLDivElement>(null);

  const wrapperClassName = [
    nav.wrapperClassName || WEBFLOW_NAV_WRAPPER_CLASS,
    WEBFLOW_NAV_WRAPPER_CLASS,
  ]
    .filter((value, index, all) => all.indexOf(value) === index)
    .join(' ');

  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest('a');
      if (!(anchor instanceof HTMLAnchorElement) || !root.contains(anchor)) {
        return;
      }

      if (!shouldHandleClientNavigation(anchor, event)) return;

      const url = new URL(anchor.href, window.location.href);
      const nextPath = `${url.pathname}${url.search}${url.hash}`;
      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (nextPath === currentPath) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      router.push(nextPath);
    };

    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
  }, [router, nav.html]);

  React.useEffect(() => {
    const root = rootRef.current;
    if (!root || permissions.isLoading) return;
    syncGuestAuthLinkVisibility(root, permissions.isAuthenticated);
  }, [permissions.isLoading, permissions.isAuthenticated, nav.html]);

  // Dynamic TW pages strip scripts from nav HTML — re-inject runtime so w-nav works.
  React.useEffect(() => {
    if (!nav.html) return;

    let cancelled = false;
    let cleanup: (() => void) | undefined;

    void injectWebflowNavRuntime(nav.scriptSrcs ?? []).then((dispose) => {
      if (cancelled) {
        dispose();
        return;
      }
      cleanup = dispose;
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [nav.html, (nav.scriptSrcs ?? []).join('|')]);

  if (!nav.html) {
    return null;
  }

  return (
    <>
      <style
        data-webflow-nav-isolation="chakra-undo"
        dangerouslySetInnerHTML={{ __html: WEBFLOW_NAV_CHAKRA_ISOLATION_CSS }}
      />
      {nav.inlineStyles.map((css, index) => (
        <style
          key={`webflow-nav-style-${index}`}
          data-webflow-nav-isolation="scoped-css"
          dangerouslySetInnerHTML={{ __html: css }}
        />
      ))}
      <style
        data-webflow-nav-isolation="hover-align"
        dangerouslySetInnerHTML={{ __html: WEBFLOW_NAV_HOVER_ALIGN_CSS }}
      />
      {preserveWebflowStyles ? (
        <style
          data-webflow-nav-isolation="native-layout"
          dangerouslySetInnerHTML={{ __html: WEBFLOW_NAV_NATIVE_LAYOUT_CSS }}
        />
      ) : (
        <style
          data-webflow-nav-isolation="theme-match"
          dangerouslySetInnerHTML={{ __html: WEBFLOW_NAV_THEME_MATCH_CSS }}
        />
      )}
      <div
        ref={rootRef}
        className={wrapperClassName}
        data-webflow-nav-root=""
        style={{ overflow: 'visible', width: '100%' }}
        dangerouslySetInnerHTML={{ __html: nav.html }}
      />
    </>
  );
};
